import {Content,} from "../../../../presentation/views/content/helpers";
import {
  ContentProvider,
  contentProviderBuilder
} from "../../../../../domain/entities/content-provider/content-provider";
import {ActionContext, ModuleTree} from "vuex";
import control from "./control";
import rating from "./rating";
import doubt from "./doubt";
import player from "./player";
import countdown from "./countdown";


import {Instructor} from "../../../../../domain/entities/entities";
import complement from "./complement";
import annotation from "./annotation";
import exercise from "./exercise";
import questionnaire from "./questionnaire";
import scorm from "./scorm";

export class ContentState {
  contentProvider: ContentProvider | null = null;
  currentContent: Content | null = null;
  instructors: Instructor[] = [];
  contents: Content[] = [];
}

const state = new ContentState();
type ContentActionContext = ActionContext<ContentState, ContentState>;

/** return the content provider granting is initialized. */
export const getContentProvider = (ctx: ActionContext<any, any>): ContentProvider => {
  const contentProvider = ctx.rootGetters["content_module/contentProvider"];
  if (!contentProvider) throw new Error("[getContentProvider] Error !contentProvider");
  return contentProvider;
};

/** return the current content granting is not null. */
export const getCurrentContent = (ctx: ActionContext<any, any>): Content => {
  const content = ctx.rootGetters["content_module/currentContent"];
  if (!content) throw new Error("[getCurrentContent] Error !content");
  return content;
};

const actions = {
  async loadContentProvider({commit}: ContentActionContext, {type, ids}) {
    try {
      const contentProvider = contentProviderBuilder.build(type, ids);
      commit("SET_CONTENT_PROVIDER", contentProvider);
    } catch (e) {
      console.error("[loadContentProvider] Error build ", e);
      throw e;
    }
  },

  async loadAllContents(ctx: ContentActionContext) {
    try {
      const provider = getContentProvider(ctx);
      const contents = await provider.getAllContents();
      ctx.commit("SET_CONTENTS", contents);
    } catch (e) {
      console.error("[loadAllContents] Error wrapContentAction ", e);
      throw e;
    }
  },

  /** TODO: Improve this method and their dependencies to reduce the redundancy. */
  async setCurrentContent(ctx: ContentActionContext, contentID: number) {
    try {
      /** initialize content if not initialized. */
      const provider = getContentProvider(ctx);
      await initializeContent(ctx, provider, contentID);

      /** Request the content by ID and update current content on state. */
      const content = await provider.getContentByID(contentID);
      ctx.commit("SET_CURRENT_CONTENT", content);

      /** update the current content on the content list. */
      await ctx.dispatch("updateContentOnList", content);
    } catch (e) {
      console.error("[setCurrentContent] Error ", e);
      throw e;
    }
  },

  async clearCurrentContent(ctx: ContentActionContext) {
    ctx.commit("SET_CURRENT_CONTENT", null);
  },

  /** Replace the equivalent content on the content array on ContentState for the giving content by comparing the id. */
  async updateContentOnList(ctx: ContentActionContext, content: Content) {
    const contents = ctx.state.contents;
    const index = contents.findIndex((it) => it.id === content.id);
    if (index === -1) throw new Error("[updateContentOnList] Error findIndex");

    contents[index] = content;
    ctx.commit("SET_CONTENTS", contents);
  },

  async loadInstructors(ctx: ContentActionContext) {
    try {
      const provider = getContentProvider(ctx);
      const content = getCurrentContent(ctx);
      const instructors = await provider.getContentInstructors(content.id) ?? [];
      ctx.commit("SET_INSTRUCTORS", instructors);
    } catch (e) {
      console.error("[loadInstructors] Error wrapContentAction ", e);
      throw e;
    }
  },

  async finishContent(ctx: ContentActionContext) {
    try {
      const provider = getContentProvider(ctx);
      const content = getCurrentContent(ctx);
      await provider.finishContent(content.id);
      content.status.progress = 100;
      await ctx.dispatch("updateContentOnList", content)
    } catch (e) {
      console.error("[finishContent] Error ", e);
      throw e;
    }
  },

  async finishContentProvider(ctx: ContentActionContext) {
    try {
      const provider = getContentProvider(ctx);
      await provider.finishContentProvider();
    } catch (e) {
      console.error("[finishContent] Error ", e);
      throw e;
    }
  },
};


const initializeContent = async (ctx: ContentActionContext, provider: ContentProvider, contentID: number): Promise<void> => {
  try {
    if (isContentInitialized(ctx, contentID)) return;
    await provider.initializeContent(contentID);
  } catch (e) {
    console.error("[initializeContent] Error initializeContent ", e);
    throw e;
  }
};

const isContentInitialized = (ctx: ContentActionContext, contentID: number): boolean => {
  const contents: Content[] = ctx.state.contents;
  const content = contents.find((content) => content.id == contentID);
  return content?.status.startedAt != null;
}

const mutations = {
  SET_CONTENT_PROVIDER(state: ContentState, contentProvider: ContentProvider) {
    state.contentProvider = contentProvider;
  },

  SET_CURRENT_CONTENT(state: ContentState, currentContent: Content | null) {
    state.currentContent = currentContent;
  },

  SET_CONTENTS(state: ContentState, contents: Content[]) {
    state.contents = contents;
  },

  SET_INSTRUCTORS(state: ContentState, instructors: Instructor[]) {
    state.instructors = instructors;
  },
};

const getters = {
  contentProvider: (state: ContentState) => state.contentProvider,
  currentContent: (state: ContentState) => state.currentContent,
  contents: (state: ContentState) => state.contents,
  instructors: (state: ContentState) => state.instructors,
};

const modules: ModuleTree<any> = {
  player,
  control,
  complement,
  rating,
  doubt,
  annotation,
  exercise,
  questionnaire,
  scorm,
  countdown,
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  modules,
};